import {useEffect} from 'react';

const APP_NAME = 'Ruta';
const SEPARATOR = '|';
const CRUMB_SEPARATOR = '>';

/**
 * The single source of truth for the browser tab title format:
 * "Ruta | {name}" (or just "Ruta" when there is no page/entity name).
 *
 * Pass an array to render the page hierarchy down to the current sub-page,
 * e.g. ['Schedule', 'Today'] → "Ruta | Schedule > Today". Empty/blank
 * segments are dropped so callers can pass a conditional sub-page safely.
 *
 * Use the `usePageTitle` hook from page components. Use this helper directly
 * only for the handful of call sites that set `document.title` imperatively
 * (e.g. map interactions) so they stay consistent with the rest of the app.
 */
export const formatDocumentTitle = (name?: string | string[] | null) => {
  const segments = (Array.isArray(name) ? name : [name])
    .filter((segment): segment is string => !!segment && segment.trim().length > 0)
    .map(segment => segment.trim());

  if (segments.length === 0) return APP_NAME;
  return `${APP_NAME} ${SEPARATOR} ${segments.join(` ${CRUMB_SEPARATOR} `)}`;
};

export const usePageTitle = (title: string | string[]) => {
  const formatted = formatDocumentTitle(title);

  useEffect(() => {
    document.title = formatted;

    return () => {
      document.title = APP_NAME;
    };
  }, [formatted]);
};